import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Button from "./Button";
import React, { SetStateAction, useEffect, useState } from "react";
import UploadFile from "./UploadFile";
import { FileWithPreview } from "@/hooks/use-file-upload";
import { useFormik } from "formik";
import { messageService } from "@/services/MessageService";
import { ApiService } from "@/services/ApiService";
import { toast } from "sonner";
import UploadStatusDialog from "./UploadStatusDialog";
import Icons from "@/assets/icons";
import { t } from "i18next";

interface UploadFileDialogDialogProps {
  open: boolean;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
  licencaId: string | null;
}

interface ExistingFile {
  id: number;
  nomeArquivo: string;
  pathArquivo: string;
}


const UploadFileDialog: React.FC<UploadFileDialogDialogProps> = ({
  open,
  setOpen,
  licencaId,
}) => {
  const [newFiles, setNewFiles] = useState<FileWithPreview[]>([]);
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [clearAll, setClearAll] = useState(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const { get, postFile, getFile, delete: deleteFile } = ApiService;

  const fetchExistingFiles = async () => {
    if (!licencaId) return;
    try {
      const { data } = await get(`api/v1/licenca/${licencaId}/arquivos`);
      setExistingFiles(data);
    } catch (error) {
      console.error("Falha ao buscar arquivos existentes:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchExistingFiles();
    } else {
      setNewFiles([]);
      setExistingFiles([]);
    }
  }, [open, licencaId]);

const handleDownload = async (fileId: number, fileName: string) => {
  setOpen(false);

  try {
    const response = await getFile(`api/v1/licenca/download/${fileId}`);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    messageService.success(
      t("fileActions.downloadSuccess.title"),
      t("fileActions.downloadSuccess.description", { fileName }),
      () => setOpen(true)
    );
  } catch (error) {
    console.error("Falha no download:", error);
    messageService.error(
      t("fileActions.downloadError.title"),
      () => setOpen(true)
    );
  }
};


 const handleDelete = async (fileId: number, fileName: string) => {
    setOpen(false);
    messageService.duasOpcoes(
      t("fileActions.deleteConfirm.title"),
      t("fileActions.deleteConfirm.description", { fileName }),
      async () => {
        try {
          await deleteFile(`api/v1/licenca/${licencaId}/arquivo/${fileId}`);
          
          toast.success(t("fileActions.deleteSuccess.title"), {
            description: t("fileActions.deleteSuccess.description", { fileName }),
          });

          setExistingFiles((prevFiles) => prevFiles.filter((f) => f.id !== fileId));
          
        } catch (error) {
          console.error("Falha ao deletar o arquivo:", error);
        } finally {
        setOpen(true);
      }
    },
    () => {
      setOpen(true);
    }
  );
};
  
  const handleClearAll = () => {
    setNewFiles([]);
    setClearAll(true);
    formikData.resetForm();
  };

  const formikData = useFormik({
    initialValues: {
      arquivos: [],
    },
    onSubmit: async () => {
      if (!licencaId) return;

      try {
        setIsUploading(true);

        if (newFiles.length === 0) {
          setOpen(false);
          messageService.error(
            t("uploadFileDialog.messages.fileError.title"),
            () => setOpen(true)
          );
          return;
        }

        const formData = new FormData();
        newFiles.forEach((f) => {
          formData.append("files", f.file as File);
        });

        const { status } = await postFile(`api/v1/licenca/${licencaId}/uploads`, formData, {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable && progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percent);
            }
          },
        });

        if (status === 200) {
          toast.success(t("uploadFileDialog.messages.toastSuccess.title"), {
            id: "upload",
            description: t("uploadFileDialog.messages.toastSuccess.description"),
          });
          fetchExistingFiles(); 
          setNewFiles([]);
        }
      } catch (e: any) {
        setOpen(false);
        messageService.errorWithDetail(
          t("uploadFileDialog.messages.uploadError.title"),
          e.response?.data?.detail ?? e,
          () => setOpen(true)
        );
      } finally {
        setUploadProgress(0);
        setIsUploading(false);
      }
    },
  });

  useEffect(() => {
    formikData.setFieldValue("arquivo", newFiles);
  }, [newFiles]);

  useEffect(() => {
    if (isUploading && !open) {
      toast.loading(t("uploadFileDialog.messages.toastLoading.title"), {
        id: "upload-progress",
        description: t("uploadFileDialog.messages.toastLoading.description"),
        action: {
          label: t("uploadFileDialog.messages.toastLoading.action"),
          onClick: () => setOpen(true),
        },
      });
    }

    if (open || !isUploading) {
      toast.dismiss("upload-progress");
    }
  }, [open, isUploading]);

  return isUploading ? (
    <UploadStatusDialog open={open && isUploading} setOpen={setOpen} uploadProgress={uploadProgress} />
  ) : (
    <Dialog open={open && !isUploading} onOpenChange={setOpen} modal={false}>
      <DialogContent
        className="!max-w-180"
        aria-describedby="Dialog para upload de arquivos"
      >
        <DialogHeader>
          <DialogTitle>{t("uploadFileDialog.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={formikData.handleSubmit}>
          <UploadFile
            setFiles={setNewFiles}
            //accept="pdf"
            classProps="mb-4"
            onDownloadClick={handleDownload}
            onDeleteClick={handleDelete}
            existingFiles={existingFiles}
            clearTrigger={clearAll}
            onClearDone={() => setClearAll(false)}
          />
          <DialogFooter>
             <Button
              type="button"
              color="neutro"
              label={t("uploadFileDialog.buttons.clear")}
              icon={<Icons.Delete pathStyle="fill-inherit" />}
              classProps="p-3"
              onClick={handleClearAll}
              disabled={newFiles.length === 0}
            />
             <Button
              type="submit"
              color="primario"
              label={t("uploadFileDialog.buttons.upload")}
              icon={<Icons.Save pathStyle="fill-inherit" />}
              classProps="p-3"
              disabled={newFiles.length === 0}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadFileDialog;